import React, { createContext, useContext, useState, useEffect } from 'react';

const PropertyContext = createContext();

export const useProperty = () => {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('useProperty must be used within a PropertyProvider');
  }
  return context;
};

export const PropertyProvider = ({ children }) => {
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [favorites, setFavorites] = useState([]);

  // Initialize with sample data and load from localStorage
  useEffect(() => {
    // Version control - clear old data if version doesn't match
    const DATA_VERSION = '2.0'; // Updated to force refresh
    const savedVersion = localStorage.getItem('truehome_data_version');
    
    if (savedVersion !== DATA_VERSION) {
      // Clear old data
      localStorage.removeItem('truehome_properties');
      localStorage.removeItem('truehome_projects');
      localStorage.setItem('truehome_data_version', DATA_VERSION);
      console.log('🔄 Data version updated - loading new property types');
    }
    
    const savedProperties = localStorage.getItem('truehome_properties');
    const savedProjects = localStorage.getItem('truehome_projects');
    
    if (savedProperties) {
      setProperties(JSON.parse(savedProperties));
    } else {
      const sampleProperties = [
        {
          id: 1,
          title: 'BDA Approved Plot - Jyothinagara',
          location: 'Jyothinagara, Bengaluru, Karnataka',
          price: 5500000,
          type: 'BDA',
          bedrooms: null,
          bathrooms: null,
          area: 2400,
          garage: null,
          images: [
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop'
          ],
          description: 'Prime BDA approved plot ready for construction. Clear title with all necessary approvals and documentation.',
          features: ['BDA Approved', 'Clear Title', 'Corner Plot', 'Near Main Road'],
          agent: {
            name: 'Rajesh Kumar',
            title: 'Land Specialist',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['Featured', 'BDA'],
          status: 'For Sale',
          yearBuilt: null,
          lotSize: 2400
        },
        {
          id: 2,
          title: 'BRMDA Residential Site - Madanayakanahalli',
          location: 'Madanayakanahalli, Bengaluru, Karnataka',
          price: 3200000,
          type: 'BRMDA',
          bedrooms: null,
          bathrooms: null,
          area: 1800,
          garage: null,
          images: [
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop'
          ],
          description: 'BRMDA approved residential site in prime location with excellent connectivity and infrastructure.',
          features: ['BRMDA Approved', 'Residential Zone', 'Water Connection', 'Electricity Available'],
          agent: {
            name: 'Priya Sharma',
            title: 'BRMDA Specialist',
            image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['Featured', 'BRMDA'],
          status: 'For Sale',
          yearBuilt: null,
          lotSize: 1800
        },
        {
          id: 3,
          title: 'DC Conversion Land - Nelamangala',
          location: 'Nelamangala, Bengaluru, Karnataka',
          price: 4500000,
          type: 'DC Conversion',
          bedrooms: null,
          bathrooms: null,
          area: 3000,
          garage: null,
          images: [
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop'
          ],
          description: 'DC conversion land suitable for residential development. Complete assistance provided for conversion process.',
          features: ['DC Conversion', 'Development Ready', 'Good Location', 'Investment Opportunity'],
          agent: {
            name: 'Arjun Reddy',
            title: 'DC Conversion Expert',
            image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['DC Conversion'],
          status: 'For Sale',
          yearBuilt: null,
          lotSize: 3000
        },
        {
          id: 4,
          title: 'Revenue Site - Doddaballapur',
          location: 'Doddaballapur, Bengaluru, Karnataka',
          price: 2800000,
          type: 'Revenue Sites',
          bedrooms: null,
          bathrooms: null,
          area: 2000,
          garage: null,
          images: [
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop'
          ],
          description: 'Revenue site with clear title and complete documentation. Ideal for residential or commercial development.',
          features: ['Revenue Site', 'Clear Documentation', 'Peaceful Location', 'Good Connectivity'],
          agent: {
            name: 'Suresh Menon',
            title: 'Revenue Site Specialist',
            image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['Revenue'],
          status: 'For Sale',
          yearBuilt: null,
          lotSize: 2000
        },
        {
          id: 5,
          title: 'Independent House - Jyothinagara',
          location: 'Jyothinagara, Bengaluru, Karnataka',
          price: 8500000,
          type: 'Houses',
          bedrooms: 3,
          bathrooms: 2,
          area: 1500,
          garage: 1,
          images: [
            'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&h=400&fit=crop'
          ],
          description: 'Spacious independent house with modern amenities and excellent ventilation. Ready to move in.',
          features: ['Independent House', 'Modular Kitchen', 'Car Parking', 'Gated Community'],
          agent: {
            name: 'Lakshmi Narayanan',
            title: 'Residential Specialist',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['Featured', 'Ready to Move'],
          status: 'For Sale',
          yearBuilt: 2022,
          lotSize: 1500
        },
        {
          id: 6,
          title: 'House for Rent - Madanayakanahalli',
          location: 'Madanayakanahalli, Bengaluru, Karnataka',
          price: 25000,
          type: 'Houses',
          bedrooms: 2,
          bathrooms: 2,
          area: 1200,
          garage: 1,
          images: [
            'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&h=400&fit=crop'
          ],
          description: 'Well-maintained house available for rent. Family-friendly neighborhood with all amenities nearby.',
          features: ['2 BHK', 'Semi-Furnished', 'Water Supply', '24/7 Security'],
          agent: {
            name: 'Maria Fernandes',
            title: 'Rental Specialist',
            image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['For Rent'],
          status: 'For Rent',
          yearBuilt: 2020,
          lotSize: 1200
        },
        {
          id: 7,
          title: 'Agricultural Land - Nelamangala',
          location: 'Nelamangala, Bengaluru, Karnataka',
          price: 6500000,
          type: 'Agriculture Land',
          bedrooms: null,
          bathrooms: null,
          area: 5000,
          garage: null,
          images: [
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop'
          ],
          description: 'Fertile agricultural land with water facility. Suitable for farming and agricultural activities.',
          features: ['Water Source', 'Fertile Soil', 'Road Access', 'Electricity Available'],
          agent: {
            name: 'Rajesh Kumar',
            title: 'Agricultural Land Expert',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['Agriculture'],
          status: 'For Sale',
          yearBuilt: null,
          lotSize: 5000
        },
        {
          id: 8,
          title: 'Commercial Shed - Doddaballapur',
          location: 'Doddaballapur, Bengaluru, Karnataka',
          price: 12000000,
          type: 'Shed',
          bedrooms: null,
          bathrooms: 2,
          area: 8000,
          garage: null,
          images: [
            'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop'
          ],
          description: 'Industrial shed suitable for warehouse or manufacturing. High ceiling with loading dock facility.',
          features: ['Industrial Shed', 'Loading Dock', 'Three Phase Power', 'Ample Parking'],
          agent: {
            name: 'Arjun Reddy',
            title: 'Commercial Specialist',
            image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['Commercial'],
          status: 'For Sale',
          yearBuilt: 2021,
          lotSize: 8000
        },
        {
          id: 9,
          title: 'Residential Land - Jyothinagara',
          location: 'Jyothinagara, Bengaluru, Karnataka',
          price: 3500000,
          type: 'Land',
          bedrooms: null,
          bathrooms: null,
          area: 2200,
          garage: null,
          images: [
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop'
          ],
          description: 'Prime residential land near main road. Perfect for building your dream home with all amenities nearby.',
          features: ['Residential Zone', 'Near Main Road', 'Clear Title', 'Peaceful Area'],
          agent: {
            name: 'Priya Sharma',
            title: 'Land Specialist',
            image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face'
          },
          badges: ['Featured', 'Prime Location'],
          status: 'For Sale',
          yearBuilt: null,
          lotSize: 2200
        }
      ];
      setProperties(sampleProperties);
      localStorage.setItem('truehome_properties', JSON.stringify(sampleProperties));
    }

    if (savedProjects) {
      setProjects(JSON.parse(savedProjects));
    } else {
      const sampleProjects = [
        {
          id: 1,
          title: 'Architecture & Construction Services',
          location: 'Bengaluru, Karnataka',
          type: 'Construction Services',
          image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&h=600&fit=crop&crop=center',
          status: 'Ongoing',
          units: 'Custom Projects',
          description: 'Complete architecture plans and construction services for houses and sheds at discounted prices. Professional design and quality construction.',
          startDate: '2020-01-01',
          completionDate: 'Ongoing',
          totalValue: 50000000,
          features: ['Architectural Design', 'BDA/BRMDA Approvals', 'Quality Construction', 'Discounted Rates']
        },
        {
          id: 2,
          title: 'BDA Layout Development - Jyothinagara',
          location: 'Jyothinagara, Bengaluru',
          type: 'BDA Layout',
          image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&h=600&fit=crop&crop=center',
          status: 'Upcoming',
          units: '80 Plots',
          description: 'New BDA approved residential layout with all modern amenities and infrastructure. Prime location with excellent connectivity.',
          startDate: '2024-06-01',
          completionDate: '2025-12-31',
          totalValue: 240000000,
          features: ['BDA Approved', 'Gated Community', 'Underground Drainage', 'Street Lights']
        }
      ];
      setProjects(sampleProjects);
      localStorage.setItem('truehome_projects', JSON.stringify(sampleProjects));
    }
  }, []);

  // Save to localStorage whenever properties or projects change
  useEffect(() => {
    if (properties.length > 0) {
      localStorage.setItem('truehome_properties', JSON.stringify(properties));
    }
  }, [properties]);

  useEffect(() => {
    if (projects.length > 0) {
      localStorage.setItem('truehome_projects', JSON.stringify(projects));
    }
  }, [projects]);

  // Load favorites and compare list from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('truehome_favorites');
    const savedCompareList = localStorage.getItem('truehome_compare');
    
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
    
    if (savedCompareList) {
      setCompareList(JSON.parse(savedCompareList));
    }
  }, []);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('truehome_favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Save compare list to localStorage
  useEffect(() => {
    localStorage.setItem('truehome_compare', JSON.stringify(compareList));
  }, [compareList]);

  const addProperty = (property) => {
    const newProperty = {
      ...property,
      id: Date.now(),
      images: property.images || ['https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&h=400&fit=crop']
    };
    setProperties(prev => [newProperty, ...prev]);
  };

  const updateProperty = (id, updatedProperty) => {
    setProperties(prev => 
      prev.map(property => 
        property.id === id ? { ...property, ...updatedProperty } : property
      )
    );
  };

  const deleteProperty = (id) => {
    setProperties(prev => prev.filter(property => property.id !== id));
  };

  const getPropertyById = (id) => {
    return properties.find(property => property.id === parseInt(id));
  };

  const searchProperties = (filters) => {
    return properties.filter(property => {
      const matchesLocation = !filters.location || 
        property.location.toLowerCase().includes(filters.location.toLowerCase());
      
      const matchesType = !filters.type || filters.type === 'All Types' || 
        property.type === filters.type;
      
      const matchesPrice = !filters.priceRange || filters.priceRange === 'Any Price' || 
        checkPriceRange(property.price, filters.priceRange);
      
      const matchesBedrooms = !filters.bedrooms || filters.bedrooms === 'Any' || 
        property.bedrooms >= parseInt(filters.bedrooms);

      // Advanced filters
      const matchesMinArea = !filters.minArea || property.area >= parseInt(filters.minArea);
      const matchesMaxArea = !filters.maxArea || property.area <= parseInt(filters.maxArea);
      const matchesYearBuilt = !filters.yearBuilt || (property.yearBuilt && property.yearBuilt >= parseInt(filters.yearBuilt));
      const matchesGarage = !filters.garage || property.garage >= parseInt(filters.garage);
      const matchesFeatures = !filters.features || filters.features.length === 0 || 
        filters.features.every(feature => property.features && property.features.includes(feature));

      return matchesLocation && matchesType && matchesPrice && matchesBedrooms && 
             matchesMinArea && matchesMaxArea && matchesYearBuilt && matchesGarage && matchesFeatures;
    });
  };

  const checkPriceRange = (price, range) => {
    const priceInCrores = price / 10000000;
    switch (range) {
      case '₹5Cr - ₹10Cr':
        return priceInCrores >= 5 && priceInCrores <= 10;
      case '₹10Cr - ₹20Cr':
        return priceInCrores >= 10 && priceInCrores <= 20;
      case '₹20Cr - ₹50Cr':
        return priceInCrores >= 20 && priceInCrores <= 50;
      case '₹50Cr+':
        return priceInCrores >= 50;
      default:
        return true;
    }
  };

  const formatPrice = (price) => {
    const crores = price / 10000000;
    if (crores >= 1) {
      return `₹${crores.toFixed(2)} Cr`;
    } else {
      const lakhs = price / 100000;
      return `₹${lakhs.toFixed(2)} L`;
    }
  };

  // Favorites functionality
  const toggleFavorite = (propertyId) => {
    setFavorites(prev => 
      prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const isFavorite = (propertyId) => {
    return favorites.includes(propertyId);
  };

  // Compare functionality
  const addToCompare = (propertyId) => {
    if (compareList.length >= 3) {
      return { success: false, message: 'You can compare maximum 3 properties' };
    }
    
    if (compareList.includes(propertyId)) {
      return { success: false, message: 'Property already in comparison' };
    }
    
    setCompareList(prev => [...prev, propertyId]);
    return { success: true, message: 'Property added to comparison' };
  };

  const removeFromCompare = (propertyId) => {
    setCompareList(prev => prev.filter(id => id !== propertyId));
  };

  const clearCompareList = () => {
    setCompareList([]);
  };

  const getCompareProperties = () => {
    return compareList.map(id => properties.find(p => p.id === id)).filter(Boolean);
  };

  // Analytics functionality
  const getAnalytics = () => {
    const totalProperties = properties.length;
    const soldProperties = properties.filter(p => p.status === 'Sold').length;
    const forSaleProperties = properties.filter(p => p.status === 'For Sale').length;
    const averagePrice = totalProperties > 0 
      ? properties.reduce((sum, p) => sum + p.price, 0) / totalProperties 
      : 0;
    
    const propertyTypes = properties.reduce((acc, property) => {
      acc[property.type] = (acc[property.type] || 0) + 1;
      return acc;
    }, {});

    const priceRanges = {
      'Under ₹10Cr': properties.filter(p => p.price < 100000000).length,
      '₹10Cr - ₹25Cr': properties.filter(p => p.price >= 100000000 && p.price < 250000000).length,
      '₹25Cr - ₹50Cr': properties.filter(p => p.price >= 250000000 && p.price < 500000000).length,
      'Above ₹50Cr': properties.filter(p => p.price >= 500000000).length,
    };

    // Project analytics
    const totalProjects = projects.length;
    const completedProjects = projects.filter(p => p.status === 'Completed').length;
    const ongoingProjects = projects.filter(p => p.status === 'Ongoing').length;
    const planningProjects = projects.filter(p => p.status === 'Planning').length;
    const totalProjectValue = projects.reduce((sum, p) => sum + (p.totalValue || 0), 0);

    return {
      totalProperties,
      soldProperties,
      forSaleProperties,
      averagePrice,
      propertyTypes,
      priceRanges,
      conversionRate: totalProperties > 0 ? ((soldProperties / totalProperties) * 100).toFixed(1) : 0,
      // Project analytics
      totalProjects,
      completedProjects,
      ongoingProjects,
      planningProjects,
      totalProjectValue
    };
  };

  // Project management functions
  const addProject = (project) => {
    const newProject = {
      ...project,
      id: Date.now(),
      image: project.image || 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&h=600&fit=crop&crop=center'
    };
    setProjects(prev => [newProject, ...prev]);
  };

  const updateProject = (id, updatedProject) => {
    setProjects(prev => 
      prev.map(project => 
        project.id === id ? { ...project, ...updatedProject } : project
      )
    );
  };

  const deleteProject = (id) => {
    setProjects(prev => prev.filter(project => project.id !== id));
  };

  const getProjectById = (id) => {
    return projects.find(project => project.id === parseInt(id));
  };

  const value = {
    properties,
    projects,
    loading,
    addProperty,
    updateProperty,
    deleteProperty,
    getPropertyById,
    searchProperties,
    formatPrice,
    setLoading,
    // Favorites
    favorites,
    toggleFavorite,
    isFavorite,
    // Compare
    compareList,
    addToCompare,
    removeFromCompare,
    clearCompareList,
    getCompareProperties,
    // Analytics
    getAnalytics,
    // Project management
    addProject,
    updateProject,
    deleteProject,
    getProjectById
  };

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
};